import {
  Button,
  Card,
  Col,
  Create,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tag,
  notification,
  useForm,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { SingleDatePicker } from 'components/date-picker/SingleDatePicker';
import { ModuleExpenseDrawer } from 'components/drawer/ModuleExpenseDrawer';
import { ProductPurchaseTable } from 'components/product-purchase-table';
import dayjs from 'dayjs';
import { EPaymentType, EStatus } from 'enums';
import { IPurchase, ISupplier } from 'interfaces';
import { useEffect, useReducer, useRef, useState } from 'react';
import {
  ActionKind,
  initialState,
  productPurchaseReducer,
} from 'reducers/product-purchase-reducer';
import { ICreateExpenseReturnType } from 'services/expense';
import { deleteExpenseCategory } from 'services/expense-category';
import { getPOCOfSupplier } from 'services/point-of-contact';
import { createPurchase } from 'services/purchase';
import { getSupplier, getSuppliersDropdown } from 'services/supplier';
import { getTransactionToken } from 'services/transaction';
import { getWarehousesDropdown, IWarehouseDropDown } from 'services/warehouse';
import { getAddressAsString } from 'utilities/shared';

interface ISupplierPOCState {
  id: string;
  name: string;
}

const { Option } = Select;

export const PurchaseCreate: React.FC = () => {
  const [warehouses, setWarehouses] = useState<IWarehouseDropDown[]>([]);
  const [suppliers, setSuppliers] = useState<
    Omit<
      ISupplier,
      'address' | 'pointOfContacts' | 'supplierPhoto' | 'supplierTags' | 'payMethods'
    >[]
  >([]);
  const [supplierPOCs, setSupplierPOCs] = useState<ISupplierPOCState[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [purchasesDate, setPurchasesDate] = useState<Date>();
  const [isExpenseVisible, setIsExpenseVisible] = useState(false);
  const [expenses, setExpenses] = useState<ICreateExpenseReturnType[]>([]);
  const [
    { purchasedProducts, warehouseId, vat, shipment, discountCost, paidInCash, payNow, fullDue },
    dispatch,
  ] = useReducer(productPurchaseReducer, initialState);
  const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState<boolean>(false);

  const { list } = useNavigation();
  const { form, formProps, saveButtonProps, queryResult } = useForm<IPurchase>({
    metaData: {
      fields: ['id'],
    },
  });

  const transactionTokenRef = useRef<string>();
  const transactionTokenRequestRef = useRef<boolean>(false);
  const warehouseSelectRef = useRef<any>(null);
  const supplierSelectRef = useRef<any>(null);
  const datePickerRef = useRef<any>(null);
  const pocSelectRef = useRef<any>(null);
  const purchaseAddressRef = useRef<any>(null);

  useEffect(() => {
    (async () => {
      setIsFetching(true);
      try {
        const [warehouseResponse, supplierResponse] = await Promise.all([
          getWarehousesDropdown(),
          getSuppliersDropdown(),
        ]);

        setSuppliers(supplierResponse);
        setWarehouses(warehouseResponse.map(({ id, name }) => ({ id, name })));

        // after fetching the data, focus on the warehouse dropdown
        setTimeout(() => {
          warehouseSelectRef.current?.focus();
          setWarehouseDropdownOpen(true);
        }, 0);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsFetching(false);
    })();
  }, []);

  const addExpenses = (createdExpense: ICreateExpenseReturnType) => {
    setExpenses((prevExpenses) => {
      return [...prevExpenses, createdExpense];
    });
  };

  const getOptions = (items: ISupplier[] | ISupplierPOCState[] | IWarehouseDropDown[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const onSelectWarehouse = async (warehouseId: string | undefined) => {
    if (!warehouseId) return;
    dispatch({ type: ActionKind.SetWarehouseId, payload: warehouseId });

    // after selecting the warehouse, focus on the client dropdown
    setTimeout(() => {
      supplierSelectRef.current?.focus();
    }, 0);
  };

  const onSelectSupplier = async (supplierId: string) => {
    // after selecting the supplier, focus on the date picker
    setTimeout(() => {
      datePickerRef.current?.focus();
    }, 0);

    try {
      const [supplier, pocs] = await Promise.all([
        getSupplier(supplierId),
        getPOCOfSupplier(supplierId),
      ]);

      const address = supplier?.address;
      const addressString = getAddressAsString(address || {});

      setSupplierPOCs([...pocs]);

      form.setFieldsValue({
        purchaseAddress: addressString,
        supplierPoc: pocs[0]?.id,
      });
    } catch (_) {
      setSupplierPOCs([]);
      form.setFieldsValue({
        purchaseAddress: undefined,
        supplierPoc: undefined,
      });
    }
  };

  const onFinish = async (values: any) => {
    if (transactionTokenRequestRef.current === true) return;

    try {
      setIsCreating(true);
      const totalAmount = purchasedProducts.reduce((total, product) => {
        total += product.quantity * product.unitPrice;
        return total;
      }, 0);
      const netTotal = totalAmount - discountCost + shipment + totalAmount * (vat / 100);

      const purchasesPaymentsPayload = paidInCash
        ? [
            {
              paymentType: EPaymentType.Cash,
              currentPaidAmount: netTotal,
              paymentDate: values.purchasesDate ? new Date(values.purchasesDate) : new Date(),
            },
          ]
        : [];

      if (!transactionTokenRef.current && transactionTokenRequestRef.current === false) {
        transactionTokenRequestRef.current = true;
        transactionTokenRef.current = await getTransactionToken();
      }

      const purchasedData = {
        purchasePayments: purchasesPaymentsPayload,
        supplier: values.supplier as string,
        supplierPoc: values.supplierPoc as string,
        warehouse: values.warehouse as string,
        purchaseAddress: values.purchaseAddress as string,
        note: values.note as string,
        purchaseDate: values.purchasesDate ? new Date(values.purchasesDate) : new Date(),
        purchaseStatus: EStatus.CONFIRMED,
        vatAmount: vat,
        shipmentCost: shipment,
        discountCost: discountCost,
        purchaseProducts: purchasedProducts.map((product) => ({
          sku: product.sku,
          quantity: product.quantity,
          unitPrice: product.unitPrice,
        })),
        purchaseExpense: expenses.map((expense) => ({
          id: expense.id,
        })),
        transactionToken: transactionTokenRef.current!,
      };

      await createPurchase(purchasedData);
      notification.success({
        message: 'Success',
        description: 'Successfully created a purchase.',
      });
      list('purchases');
    } catch (err: any) {
      transactionTokenRef.current = undefined;
      transactionTokenRequestRef.current = false;
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const renderedExpenses = expenses.map((expense) => {
    const paymentMethodBankName = expense.paymentMethod ? `-${expense.paymentMethod.bankName}` : '';
    const tagString = `${expense.category.name}${paymentMethodBankName}-${expense.amount}`;
    return (
      <Tag
        key={expense.id}
        closable
        onClose={() => {
          try {
            deleteExpenseCategory(expense.id);
            setExpenses((prevExpenses) => {
              return prevExpenses.filter((item) => item.id !== expense.id);
            });
          } catch (err) {
            if (err instanceof Error) {
              notification.error({
                message: 'Failed',
                description: err.message,
              });
            }
          }
        }}
      >
        {tagString}
      </Tag>
    );
  });

  return (
    <>
      <ModuleExpenseDrawer
        onClose={() => setIsExpenseVisible(false)}
        visible={isExpenseVisible}
        addExpenses={addExpenses}
        title="Add Expense"
      />

      <Create
        isLoading={queryResult?.isFetching}
        saveButtonProps={{
          ...saveButtonProps,
          disabled: !purchasedProducts.length,
          loading: isCreating,
        }}
      >
        <Form
          {...formProps}
          layout="vertical"
          initialValues={{
            isActive: true,
          }}
          onFinish={onFinish}
        >
          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <Card title="Purchase Information">
                <Form.Item
                  label="Warehouse"
                  name="warehouse"
                  rules={[{ required: true, message: 'Please select a warehouse' }]}
                >
                  <Select
                    ref={warehouseSelectRef}
                    autoFocus
                    open={warehouseDropdownOpen}
                    showSearch
                    placeholder="Select a warehouse"
                    loading={isFetching}
                    disabled={isFetching || !warehouses.length}
                    onSelect={(value: string) => {
                      setWarehouseDropdownOpen(false);
                      onSelectWarehouse(value?.toString());
                    }}
                    onDropdownVisibleChange={(visible) => setWarehouseDropdownOpen(visible)}
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {getOptions(warehouses)}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Supplier's Name"
                  name="supplier"
                  rules={[{ required: true, message: 'Please select a supplier' }]}
                >
                  <Select
                    showSearch
                    ref={supplierSelectRef}
                    placeholder="Select a supplier"
                    onSelect={onSelectSupplier}
                    loading={isFetching}
                    disabled={isFetching || !suppliers.length}
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {getOptions(suppliers)}
                  </Select>
                </Form.Item>
                <Form.Item label="Date" name="purchasesDate">
                  <SingleDatePicker
                    ref={datePickerRef}
                    value={dayjs(purchasesDate)}
                    onChange={(value: any, formatString: string) => {
                      setPurchasesDate(new Date(value.toDate()));
                      pocSelectRef.current?.focus();
                    }}
                  />
                </Form.Item>
                <Form.Item label="Point Of Contact" name="supplierPoc">
                  <Select
                    ref={pocSelectRef}
                    showSearch
                    placeholder="Select a POC"
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    onDropdownVisibleChange={(visible) => {
                      if (!visible) {
                        purchaseAddressRef.current?.focus();
                      }
                    }}
                  >
                    {getOptions(supplierPOCs)}
                  </Select>
                </Form.Item>
                <Form.Item label="Purchase Address" name="purchaseAddress">
                  <Input.TextArea ref={purchaseAddressRef} rows={4} />
                </Form.Item>
                <Form.Item label="Note" name="note">
                  <Input.TextArea rows={4} />
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <Divider />

          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Button onClick={() => setIsExpenseVisible(true)} size="large">
                  Add Expense
                </Button>
              </div>
            </Col>
          </Row>

          <Row justify="center" style={{ marginTop: '1rem' }}>
            <Col span={12}>
              <Space size={[8, 16]} wrap>
                {renderedExpenses}
              </Space>
            </Col>
          </Row>

          <Divider />

          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <ProductPurchaseTable
                purchasedProducts={purchasedProducts}
                warehouse={warehouseId}
                vat={vat}
                shipment={shipment}
                discountCost={discountCost}
                paidInCash={paidInCash}
                payNow={payNow}
                fullDue={fullDue}
                productDispatcher={dispatch}
              />
            </Col>
          </Row>
        </Form>
      </Create>
    </>
  );
};
